<script setup lang="ts">
import { Select } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { RowView } from '@/features/entries'
import {
  type CarEventDraft,
  type CarEventMatch,
  resolveCarEvent,
} from '../composables/use-car-event-extension.ts'
import { CAR_EVENT_TYPES, type CarEventData, type CarEventType } from '../internal/types.ts'
import EventTypeFields from '@/features/garage/components/EventTypeFields.vue'

const props = defineProps<{ match: CarEventMatch; draft: CarEventDraft; view: RowView }>()
const emit = defineEmits<{ 'update:draft': [CarEventDraft] }>()

const { t } = useI18n()

const FIELD_LABEL_CLASS = 'text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]'
const FIELD_INPUT_CLASS =
  'h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]'

/** The events a cost can record; buying, selling and a bare mileage reading are not costs. */
const COST_TYPES = CAR_EVENT_TYPES.filter(
  (type) => type !== 'MILEAGE_LOG' && type !== 'SELL' && type !== 'BUY',
)

const resolved = computed(() => resolveCarEvent(props.match, props.draft))
const car = computed(() => props.match.cars.find((candidate) => candidate.id === resolved.value.carId))
const carOptions = computed(() =>
  props.match.cars.map((candidate) => ({ value: candidate.id, label: candidate.name })),
)
const typeOptions = computed(() =>
  COST_TYPES.map((type) => ({ value: type, label: t(`garage.eventType.${type}`) })),
)
const fieldData = computed(() => {
  const type = resolved.value.type
  return type === undefined
    ? undefined
    : props.draft.data?.type === type
      ? props.draft.data
      : resolved.value.data
})

function update(patch: Partial<CarEventDraft>): void {
  emit('update:draft', { ...props.draft, ...patch })
}
</script>

<template>
  <div class="flex flex-col gap-[var(--space-cell)]">
    <label v-if="match.cars.length > 1" class="flex flex-col gap-1">
      <span :class="FIELD_LABEL_CLASS">{{ t('garage.extension.car') }}</span>
      <Select
        :model-value="resolved.carId"
        :options="carOptions"
        :placeholder="t('garage.extension.chooseCar')"
        @update:model-value="(carId: string) => update({ carId })"
      />
    </label>

    <label v-if="match.inferredType === undefined" class="flex flex-col gap-1">
      <span :class="FIELD_LABEL_CLASS">{{ t('garage.extension.type') }}</span>
      <Select
        :model-value="draft.type"
        :options="typeOptions"
        :placeholder="t('garage.extension.chooseType')"
        @update:model-value="(type: CarEventType) => update({ type })"
      />
    </label>

    <label class="flex flex-col gap-1">
      <span :class="FIELD_LABEL_CLASS">{{ t('garage.eventField.mileage') }}</span>
      <input
        :value="draft.mileageKm"
        type="number"
        min="0"
        :class="FIELD_INPUT_CLASS"
        @input="(event) => update({ mileageKm: (event.target as HTMLInputElement).value })"
      />
    </label>

    <EventTypeFields
      v-if="car !== undefined && resolved.type !== undefined && fieldData !== undefined"
      :key="`${car.id}:${resolved.type}`"
      :type="resolved.type"
      :model-value="fieldData"
      :car="car"
      :policies="match.policiesByCar.get(car.id) ?? []"
      :schedules="match.schedulesByCar.get(car.id) ?? []"
      :is-editing="true"
      @update:model-value="(data: CarEventData) => update({ data })"
    />
    <p
      v-if="resolved.type === 'FINE'"
      class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
    >
      {{ t('garage.extension.finePaid') }}
    </p>
  </div>
</template>
